import { Component } from '@theme/component'

/**
 * A custom element that displays a sticky add to cart section.
 *
 * @typedef {Object} StickyATCRefs
 * @property {HTMLDivElement} container - The container element.
 * @property {HTMLDivElement} mobileVariantPicker - The mobile variant picker element.
 * @property {HTMLButtonElement} mobileVariantPickerToggle - The mobile variant picker toggle button.
 *
 * example usage:
 * ```html
 * <sticky-atc>
 *  <div ref="container">content</div>
 *  <div ref="mobileVariantPicker">content</div>
 *  <button ref="mobileVariantPickerToggle">Toggle mobile variant picker</button>
 * </sticky-atc>
 * ```
 *
 * @extends {Component<StickyATCRefs>}
 */
class StickyATC extends Component {
  requiredRefs = [
    'container',
    'mobileVariantPicker',
    'mobileVariantPickerToggle',
  ]

  #desktopMql = window.matchMedia('(min-width: 990px)')

  /** @type {boolean} */
  #mobileListenersAttached = false

  /**
   * Primary purchase controls in the product information section.
   * Prefer buy-buttons so sticky ATC appears as soon as ATC leaves view,
   * not after the entire (often tall) product-information section does.
   * @type {Element | null}
   */
  #primaryPurchaseControls =
    document.querySelector('.product-information .buy-buttons-block') ??
    document.querySelector('.product-information product-form-component') ??
    document.querySelector('.product-information')

  /** @type {IntersectionObserver | null} */
  #primaryPurchaseObserver = null

  /** @type {NodeListOf<HTMLButtonElement>} */
  #addToCartButtons = this.querySelectorAll('.sticky-atc__add-to-cart-button')

  connectedCallback() {
    super.connectedCallback()

    if (this.#primaryPurchaseControls) {
      this.#primaryPurchaseObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // Keep sticky hidden while primary controls are in view, or still
          // below the fold. Show only after the user has scrolled past them.
          const shouldHideSticky =
            entry.isIntersecting || entry.boundingClientRect.top >= 0
          this.refs.container.toggleAttribute('inert', shouldHideSticky)
        })
      })
      this.#primaryPurchaseObserver.observe(this.#primaryPurchaseControls)
    }

    this.#syncMobilePickerBehavior()
    this.#desktopMql.addEventListener('change', this.#syncMobilePickerBehavior)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.#primaryPurchaseObserver) {
      this.#primaryPurchaseObserver.disconnect()
    }

    this.#desktopMql.removeEventListener(
      'change',
      this.#syncMobilePickerBehavior
    )
    this.#detachMobilePickerListeners()
  }

  #syncMobilePickerBehavior = () => {
    if (this.#desktopMql.matches) {
      this.refs.mobileVariantPicker.removeAttribute('inert')
      this.refs.container.classList.remove('hidden')
      this.#detachMobilePickerListeners()
      return
    }

    this.refs.mobileVariantPicker.setAttribute('inert', '')
    this.refs.container.classList.remove('hidden')
    this.#attachMobilePickerListeners()
  }

  #attachMobilePickerListeners = () => {
    if (this.#mobileListenersAttached) {
      return
    }
    this.#mobileListenersAttached = true

    this.#addToCartButtons.forEach((button) => {
      button.addEventListener('click', this.#closeMobileVariantPicker)
    })

    this.refs.mobileVariantPickerToggle.addEventListener(
      'click',
      this.#toggleMobileVariantPicker
    )
    document.addEventListener('click', this.#handleClickOutside)
  }

  #detachMobilePickerListeners = () => {
    if (!this.#mobileListenersAttached) {
      return
    }
    this.#mobileListenersAttached = false

    this.#addToCartButtons.forEach((button) => {
      button.removeEventListener('click', this.#closeMobileVariantPicker)
    })

    this.refs.mobileVariantPickerToggle.removeEventListener(
      'click',
      this.#toggleMobileVariantPicker
    )
    document.removeEventListener('click', this.#handleClickOutside)
  }

  #toggleMobileVariantPicker = () => {
    if (this.#desktopMql.matches) {
      return
    }
    const isInert = this.refs.mobileVariantPicker.hasAttribute('inert')
    this.refs.mobileVariantPicker.toggleAttribute('inert', !isInert)
  }

  #closeMobileVariantPicker = () => {
    if (this.#desktopMql.matches) {
      return
    }
    this.refs.mobileVariantPicker.setAttribute('inert', '')
  }

  /**
   * Handles the click outside event to close the mobile variant picker.
   * @param {MouseEvent} event - The click event.
   */
  #handleClickOutside = (event) => {
    if (this.#desktopMql.matches) {
      return
    }
    if (!(event.target instanceof Node)) {
      return
    }

    if (
      this.refs.mobileVariantPickerToggle.contains(event.target) ||
      this.refs.mobileVariantPicker.hasAttribute('inert')
    ) {
      return
    }

    if (!this.refs.mobileVariantPicker.contains(event.target)) {
      this.refs.mobileVariantPicker.setAttribute('inert', '')
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyATC)
}
